import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  CloudUpload,
  Download,
  FileText,
  Folder,
  Loader2,
  Lock,
  MessageSquare,
} from 'lucide-react';
import clsx from 'clsx';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import BrandLogo from '../../components/BrandLogo';
import InlinePreview from '../files/components/InlinePreview';
import PublicShareChatPanel from '../chat/PublicShareChatPanel';
import PublicShareCommentsPanel from '../comments/PublicShareCommentsPanel';
import { useAuthStore } from '../../store/authStore';
import { formatBytes } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import {
  accessShare,
  downloadSharedFile,
  fetchSharedPreviewBlob,
  resolveShareMeta,
} from './shareApi';

export default function PublicSharePage() {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [payload, setPayload] = useState(null);

  const meta = useQuery({
    queryKey: ['share-meta', token],
    queryFn: () => resolveShareMeta(token),
    retry: false,
  });

  const accessMutation = useMutation({
    mutationFn: () => accessShare(token, password),
    onSuccess: (data) => setPayload(data),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not open share')),
  });

  // Auto-open links that are not password protected (once, after metadata loads).
  const { isSuccess: metaReady, data: metaData } = meta;
  const { mutate: openShare, isIdle: accessIdle } = accessMutation;
  useEffect(() => {
    if (metaReady && metaData && !metaData.requiresPassword && !payload && accessIdle) {
      openShare();
    }
  }, [metaReady, metaData, payload, accessIdle, openShare]);

  const handleDownload = async (file) => {
    const id = toast.loading(`Downloading ${file.name}…`);
    try {
      await downloadSharedFile({ token, fileId: file.id, name: file.name, password });
      toast.success('Download ready', { id });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Download failed'), { id });
    }
  };

  return (
    <div className="flex min-h-full items-start justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex justify-center"><BrandLogo className="h-10 w-auto max-w-full" /></div>

        <div className="rounded-2xl bg-slate-100 p-6 shadow-lg ring-1 ring-slate-100">
          {meta.isLoading ? (
            <Spinner />
          ) : meta.isError ? (
            <p className="py-8 text-center text-sm text-red-600">
              {getErrorMessage(meta.error, 'This link is invalid or has expired.')}
            </p>
          ) : meta.data.requiresPassword && !payload ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <Lock className="h-8 w-8 text-slate-400" />
                <h1 className="text-base font-semibold text-slate-800">
                  Password required
                </h1>
                <p className="text-sm text-slate-500">
                  Enter the password to access &ldquo;{meta.data.name}&rdquo;.
                </p>
              </div>
              <Input
                type="password"
                label="Password"
                value={password}
                autoFocus
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && password && accessMutation.mutate()}
              />
              <Button
                className="w-full"
                loading={accessMutation.isPending}
                disabled={!password}
                onClick={() => accessMutation.mutate()}
              >
                Unlock
              </Button>
            </div>
          ) : payload ? (
            <>
              <SharePayload
                payload={payload}
                token={token}
                password={password}
                onDownload={handleDownload}
              />
              {payload.allowChat && (
                <ShareConversation token={token} password={password} />
              )}
            </>
          ) : (
            <Spinner />
          )}
        </div>
      </div>
    </div>
  );
}

function ShareConversation({ token, password }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const [tab, setTab] = useState('comments');

  if (!isAuthenticated) {
    return (
      <div className="mt-6 flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-slate-700">
        <MessageSquare className="h-4 w-4 shrink-0 text-brand-600" />
        <span className="flex-1">Chat &amp; comments are available on this link.</span>
        <Link
          to="/login"
          state={{ from: location }}
          className="font-medium text-brand-600 hover:text-brand-700"
        >
          Log in to join
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="mb-3 flex border-b border-slate-200">
        {[
          { key: 'comments', label: 'Comments' },
          { key: 'chat', label: 'Chat' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              'px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-b-2 border-brand-500 text-slate-800'
                : 'text-slate-400 hover:text-slate-600',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'comments' ? (
        <PublicShareCommentsPanel token={token} password={password} />
      ) : (
        <PublicShareChatPanel token={token} password={password} />
      )}
    </div>
  );
}

function SharePayload({ payload, token, password, onDownload }) {
  const canDownload = payload.accessMode !== 'view';
  const isFile = payload.resourceType === 'file';
  const file = isFile ? payload.file : null;

  const preview = useQuery({
    queryKey: ['share-preview', token, file?.id],
    queryFn: () => fetchSharedPreviewBlob({ token, fileId: file.id, password }),
    enabled: isFile,
  });

  if (isFile) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800">{file.name}</p>
            <p className="text-sm text-slate-500">{formatBytes(file.sizeBytes)}</p>
          </div>
          {canDownload && (
            <Button onClick={() => onDownload(file)}>
              <Download className="h-4 w-4" />
              Download
            </Button>
          )}
        </div>
        <div className="h-[60vh] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {preview.isLoading ? (
            <div className="flex h-full items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : preview.isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <FileText className="h-10 w-10" />
              <p className="text-sm">Couldn’t load a preview.</p>
            </div>
          ) : (
            <InlinePreview
              mimeType={file.mimeType}
              name={file.name}
              blob={preview.data}
              allowCopy={canDownload}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Folder className="h-5 w-5 text-amber-500" />
        <h1 className="font-semibold text-slate-800">{payload.folder.name}</h1>
      </div>
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-100">
        {payload.folders.map((f) => (
          <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600">
            <Folder className="h-4 w-4 text-amber-500" />
            {f.name}
          </div>
        ))}
        {payload.files.map((file) => (
          <div key={file.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <FileText className="h-4 w-4 text-slate-400" />
            <span className="flex-1 truncate text-slate-700">{file.name}</span>
            <span className="text-xs text-slate-400">{formatBytes(file.sizeBytes)}</span>
            {canDownload && (
              <button
                className="rounded p-1 text-slate-400 hover:text-brand-600"
                onClick={() => onDownload(file)}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {payload.folders.length === 0 && payload.files.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-slate-400">This folder is empty.</div>
        )}
      </div>
    </div>
  );
}
